import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import { apiInstance } from '../api/api';

const emptyBranch = {
    name: '',
    code: '',
    store_type: 'OUTLET',
    parent_store: '',
    is_active: true,
    receipt_area: '',
    receipt_phone_number: '',
    receipt_payment_details: '',
    printnode_printer_id: '',
};

const usageTypes = [
    { value: 'PAYMENT', label: 'Customer payment' },
    { value: 'EXPENSE_PAYMENT', label: 'Expense payment' },
    { value: 'EXPENSE', label: 'Expense account' },
    { value: 'RECEIVABLE', label: 'Customer receivable' },
    { value: 'SALES', label: 'Sales account' },
    { value: 'BANK', label: 'Bank account' },
    { value: 'GENERAL', label: 'General' },
];
const paymentChannels = [
    { value: 'CASH', label: 'Cash' },
    { value: 'TILL', label: 'Till / Paybill' },
    { value: 'BANK', label: 'Bank' },
    { value: 'OTHER', label: 'Other' },
]

const emptyAccountSetting = {
    store: '',
    account: '',
    usage_type: 'PAYMENT',
    is_enabled: true,
    is_default: false,
    label_override: '',
    payment_channel: 'OTHER',
};

const normalizeList = (data) => data?.results || data || [];

const formatApiError = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.detail) return data.detail;
    if (data && typeof data === 'object') {
        return Object.entries(data)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join(' ');
    }
    return fallback;
};

function Branches(){
    const [stores, setStores] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [accountSettings, setAccountSettings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [editingStoreId, setEditingStoreId] = useState(null);
    const [editingSettingId, setEditingSettingId] = useState(null);
    const [selectedSettingsStoreId, setSelectedSettingsStoreId] = useState('');
    const [form, setForm] = useState(emptyBranch);
    const [accountSettingForm, setAccountSettingForm] = useState(emptyAccountSetting);

    const parentOptions = useMemo(
        () => stores.filter((store) => String(store.id) !== String(editingStoreId)),
        [stores, editingStoreId]
    );

    const selectedSettingsStore = useMemo(
        () => stores.find((store) => String(store.id) === String(selectedSettingsStoreId)),
        [stores, selectedSettingsStoreId]
    );

    const usageLabel = (value) => usageTypes.find((usage) => usage.value === value)?.label || value;

    const accountLabel = (account) => {
        const typeName = account.account_type_details?.account_type_name || account.account_type_name;
        return typeName ? `${account.account_name} (${typeName})` : account.account_name;
    };

    const accountOptions = useMemo(
        () => accounts.map((account) => ({
                value: account.id,
                label: accountLabel(account),
            })),
            [accounts]
    );

    const loadStores = async () => {
        setLoading(true);
        try {
            const response = await apiInstance.get('/stores/');
            const storeList = normalizeList(response.data);
            setStores(storeList);
            setSelectedSettingsStoreId((current) => current || storeList[0]?.id || '');
        } catch (error) {
            setMessage({
                type: 'danger',
                text: formatApiError(error, 'Failed to load branches.'),
            });
        } finally {
            setLoading(false);
        }
    };

    const loadAccounts = async () => {
        try {
            const response = await apiInstance.get('/accounts/');
            setAccounts(normalizeList(response.data));
        } catch (error) {
            setMessage({
                type: 'danger',
                text: formatApiError(error, 'Failed to load accounts.'),
            });
        }
    };

    const loadAccountSettings = async (storeId = selectedSettingsStoreId) => {
        if (!storeId) {
            setAccountSettings([]);
            return;
        }
        setSettingsLoading(true);
        try {
            const response = await apiInstance.get(`/store-account-settings/?store_id=${storeId}`);
            setAccountSettings(normalizeList(response.data));
        } catch (error) {
            setMessage({
                type: 'danger',
                text: formatApiError(error, 'Failed to load branch account settings.'),
            });
        } finally {
            setSettingsLoading(false);
        }
    };

    useEffect(() => {
        loadStores();
        loadAccounts();
    }, []);

    useEffect(() => {
        loadAccountSettings(selectedSettingsStoreId);
    }, [selectedSettingsStoreId]);

    const openCreateModal = () => {
        setEditingStoreId(null);
        setForm(emptyBranch);
        setShowModal(true);
    };

    const openEditModal = (store) => {
        setEditingStoreId(store.id);
        setForm({
            name: store.name || '',
            code: store.code || '',
            store_type: store.store_type || 'OUTLET',
            parent_store: store.parent_store || '',
            is_active: Boolean(store.is_active),
            receipt_area: store.receipt_area || '',
            receipt_phone_number: store.receipt_phone_number || '',
            receipt_payment_details: store.receipt_payment_details || '',
            printnode_printer_id: store.printnode_printer_id || '',
        });
        setShowModal(true);
    };

    const openAccountSettingModal = (setting = null) => {
        setEditingSettingId(setting?.id || null);
        setAccountSettingForm(setting ? {
            store: setting.store || selectedSettingsStoreId || '',
            account: setting.account || '',
            usage_type: setting.usage_type || 'PAYMENT',
            is_enabled: Boolean(setting.is_enabled),
            is_default: Boolean(setting.is_default),
            label_override: setting.label_override || '',
            payment_channel: setting.payment_channel || 'OTHER',
        } : {
            ...emptyAccountSetting,
            store: selectedSettingsStoreId || stores[0]?.id || '',
        });
        setShowAccountModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingStoreId(null);
        setForm(emptyBranch);
    };

    const closeAccountSettingModal = () => {
        setShowAccountModal(false);
        setEditingSettingId(null);
        setAccountSettingForm(emptyAccountSetting);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage(null);

        const payload = {
            ...form,
            parent_store: form.parent_store || null,
        };

        try {
            if (editingStoreId) {
                await apiInstance.put(`/stores/${editingStoreId}/`, payload);
                setMessage({ type: 'success', text: 'Branch settings updated.' });
            } else {
                await apiInstance.post('/stores/', payload);
                setMessage({ type: 'success', text: 'Branch created.' });
            }
            closeModal();
            await loadStores();
        } catch (error) {
            const fallback = editingStoreId ? 'Failed to update branch.' : 'Failed to create branch.';
            setMessage({
                type: 'danger',
                text: formatApiError(error, fallback),
            });
        }
    };

    const handleAccountSettingSubmit = async (event) => {
        event.preventDefault();
        setMessage(null);

        const payload = {
            ...accountSettingForm,
            store: accountSettingForm.store || selectedSettingsStoreId,
            label_override: accountSettingForm.label_override || '',
        };

        if (!payload.account) {
            setMessage({ type: 'danger', text: 'Select an account before saving the rule.'});
            return;
        }

        try {
            if (editingSettingId) {
                await apiInstance.patch(`/store-account-settings/${editingSettingId}/`, payload);
                setMessage({ type: 'success', text: 'Branch account rule updated.' });
            } else {
                await apiInstance.post('/store-account-settings/', payload);
                setMessage({ type: 'success', text: 'Branch account rule created.' });
            }
            closeAccountSettingModal();
            setSelectedSettingsStoreId(payload.store);
            await loadAccountSettings(payload.store);
        } catch (error) {
            const fallback = editingSettingId ? 'Failed to update account rule.' : 'Failed to create account rule.';
            setMessage({
                type: 'danger',
                text: formatApiError(error, fallback),
            });
        }
    };

    const deleteAccountSetting = async (setting) => {
        if (!window.confirm(`Remove ${setting.account_name} from ${usageLabel(setting.usage_type)}?`)) return;

        try {
            await apiInstance.delete(`/store-account-settings/${setting.id}/`);
            setMessage({ type: 'success', text: 'Branch account rule removed.' });
            await loadAccountSettings(selectedSettingsStoreId);
        } catch (error) {
            setMessage({
                type: 'danger',
                text: formatApiError(error, 'Failed to remove account rule.'),
            });
        }
    };

    const getParentName = (parentId) => {
        if (!parentId) return '-';
        return stores.find((store) => String(store.id) === String(parentId))?.name || parentId;
    };

    return (
        <div className="container-fluid py-4">
            <Row className="align-items-center mb-4">
                <Col>
                    <h3 className="fw-bold mb-1">Branches</h3>
                    <p className="text-muted mb-0">Store details, receipt settings, printer routing, and account usage controls.</p>
                </Col>
                <Col xs="auto">
                    <Button variant="primary" onClick={openCreateModal}>New Branch</Button>
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            {loading ? (
                <Spinner animation="border" />
            ) : (
                <Table responsive hover>
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Type</th>
                        <th>Parent</th>
                        <th>Area</th>
                        <th>Phone</th>
                        <th>Printer ID</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    {stores.length ? stores.map((store) => (
                        <tr key={store.id}>
                            <td>{store.name}</td>
                            <td>{store.code}</td>
                            <td>{store.store_type}</td>
                            <td>{store.parent_store_name || getParentName(store.parent_store)}</td>
                            <td>{store.receipt_area || '-'}</td>
                            <td>{store.receipt_phone_number || '-'}</td>
                            <td>{store.printnode_printer_id || '-'}</td>
                            <td>
                                <Badge bg={store.is_active ? 'success' : 'secondary'}>
                                    {store.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            </td>
                            <td>
                                <Button size="sm" variant="outline-primary" onClick={() => openEditModal(store)}>
                                    Edit
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan="9" className="text-center text-muted">No branches available</td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            )}

            <div className="border-top mt-4 pt-4">
                <Row className="align-items-end g-3 mb-3">
                    <Col md={5}>
                        <h5 className="fw-bold mb-1">Branch Account Rules</h5>
                        <p className="text-muted mb-0">Limit which global accounts each branch should use for payments, expenses, receivables, and reporting trails.</p>
                    </Col>
                    <Col md={4}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={selectedSettingsStoreId}
                            onChange={(event) => setSelectedSettingsStoreId(event.target.value)}
                        >
                            {stores.map((store) => (
                                <option key={store.id} value={store.id}>{store.name}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={3} className="text-md-end">
                        <Button
                            variant="outline-primary"
                            onClick={() => openAccountSettingModal()}
                            disabled={!stores.length || !accounts.length}
                        >
                            Add Account Rule
                        </Button>
                    </Col>
                </Row>

                {settingsLoading ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <Table responsive hover>
                        <thead>
                        <tr>
                            <th>Usage</th>
                            <th>Account</th>
                            <th>Type</th>
                            <th>Receipt/Form Label</th>
                            <th>Payment Channel</th>
                            <th>Default</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {accountSettings.length ? accountSettings.map((setting) => (
                            <tr key={setting.id}>
                                <td>{usageLabel(setting.usage_type)}</td>
                                <td>{setting.account_name}</td>
                                <td>{setting.account_type_name || '-'}</td>
                                <td>{setting.label_override || '-'}</td>
                                <td>{setting.usage_type === 'PAYMENT'
                                    ? paymentChannels.find((channel) => channel.value === setting.payment_channel)?.label || 'Other'
                                    : '-'}</td>
                                <td>
                                    <Badge bg={setting.is_default ? 'primary' : 'light'} text={setting.is_default ? undefined : 'dark'}>
                                        {setting.is_default ? 'Default' : 'Optional'}
                                    </Badge>
                                </td>
                                <td>
                                    <Badge bg={setting.is_enabled ? 'success' : 'secondary'}>
                                        {setting.is_enabled ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </td>
                                <td className="d-flex gap-2">
                                    <Button size="sm" variant="outline-primary" onClick={() => openAccountSettingModal(setting)}>
                                        Edit
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => deleteAccountSetting(setting)}>
                                        Remove
                                    </Button>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="8" className="text-center text-muted">
                                    {selectedSettingsStore ? `No account rules configured for ${selectedSettingsStore.name}` : 'Select a branch to manage account rules'}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                )}
            </div>

            <Modal show={showModal} onHide={closeModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingStoreId ? 'Edit Branch' : 'New Branch'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    value={form.name}
                                    onChange={(e) => setForm({...form, name: e.target.value})}
                                    required
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    value={form.code}
                                    onChange={(e) => setForm({...form, code: e.target.value})}
                                    required
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Type</Form.Label>
                                <Form.Select
                                    value={form.store_type}
                                    onChange={(e) => setForm({...form, store_type: e.target.value})}
                                    required
                                >
                                    <option value="OUTLET">Outlet</option>
                                    <option value="WAREHOUSE">Warehouse</option>
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Parent Store</Form.Label>
                                <Form.Select
                                    value={form.parent_store || ''}
                                    onChange={(e) => setForm({...form, parent_store: e.target.value})}
                                >
                                    <option value="">No parent</option>
                                    {parentOptions.map((store) => (
                                        <option key={store.id} value={store.id}>{store.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>PrintNode Printer ID</Form.Label>
                                <Form.Control
                                    value={form.printnode_printer_id}
                                    onChange={(e) => setForm({...form, printnode_printer_id: e.target.value})}
                                    placeholder="73969712"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Receipt Area</Form.Label>
                                <Form.Control
                                    value={form.receipt_area}
                                    onChange={(e) => setForm({...form, receipt_area: e.target.value})}
                                    placeholder="Ngong Town, Kajiado"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label>Receipt Phone Number</Form.Label>
                                <Form.Control
                                    value={form.receipt_phone_number}
                                    onChange={(e) => setForm({...form, receipt_phone_number: e.target.value})}
                                    placeholder="0789525313"
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Label>Receipt Payment Details</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={form.receipt_payment_details}
                                    onChange={(e) => setForm({...form, receipt_payment_details: e.target.value})}
                                    placeholder="BUY GOODS TILL NUMBER: 4030382"
                                />
                            </Col>
                            <Col md={12}>
                                <Form.Check
                                    type="switch"
                                    label="Active"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({...form, is_active: e.target.checked})}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={closeModal}>Cancel</Button>
                        <Button variant="primary" type="submit">Save</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showAccountModal} onHide={closeAccountSettingModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingSettingId ? 'Edit Account Rule' : 'New Account Rule'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAccountSettingSubmit}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Label>Branch</Form.Label>
                                <Form.Select
                                    value={accountSettingForm.store}
                                    onChange={(event) => setAccountSettingForm({...accountSettingForm, store: event.target.value})}
                                    required
                                >
                                    <option value="">Select branch</option>
                                    {stores.map((store) => (
                                        <option key={store.id} value={store.id}>{store.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <Form.Label>Usage</Form.Label>
                                <Form.Select
                                    value={accountSettingForm.usage_type}
                                    onChange={(event) => setAccountSettingForm({...accountSettingForm, usage_type: event.target.value})}
                                    required
                                >
                                    {usageTypes.map((usage) => (
                                        <option key={usage.value} value={usage.value}>{usage.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={12}>
                                <Form.Label>Account</Form.Label>
                                <Select
                                    options={accountOptions}
                                    value={accountOptions.find(
                                        (option) => String(option.value) === String(accountSettingForm.account)
                                    ) || null}
                                    onChange={(option) => setAccountSettingForm({
                                        ...accountSettingForm,
                                        account: option?.value || '',
                                    })}
                                    placeholder="Search account"
                                    isClearable
                                    isSearchable
                                />
                                {!accountSettingForm.account && (
                                    <Form.Text className="text-danger">Select an account before saving.</Form.Text>
                                )}
                            </Col>
                            <Col md={12}>
                                <Form.Label>Label Override</Form.Label>
                                <Form.Control
                                    value={accountSettingForm.label_override}
                                    onChange={(event) => setAccountSettingForm({...accountSettingForm, label_override: event.target.value})}
                                    placeholder="Optional branch-facing label"
                                />
                            </Col>
                            {accountSettingForm.usage_type === 'PAYMENT' && (
                                <Col md={6}>
                                    <Form.Label>Payment Channel</Form.Label>
                                    <Form.Select
                                        value={accountSettingForm.payment_channel}
                                        onChange={(event) => setAccountSettingForm({
                                            ...accountSettingForm,
                                            payment_channel: event.target.value,
                                        })}
                                    >
                                        {paymentChannels.map((channel) => (
                                            <option key={channel.value} value={channel.value}>{channel.label}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            )}
                            <Col md={6}>
                                <Form.Check
                                    type="switch"
                                    label="Enabled for this branch"
                                    checked={accountSettingForm.is_enabled}
                                    onChange={(event) => setAccountSettingForm({...accountSettingForm, is_enabled: event.target.checked})}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    type="switch"
                                    label="Default for this usage"
                                    checked={accountSettingForm.is_default}
                                    onChange={(event) => setAccountSettingForm({...accountSettingForm, is_default: event.target.checked})}
                                />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={closeAccountSettingModal}>Cancel</Button>
                        <Button variant="primary" type="submit">Save Rule</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}

export default Branches;
